import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { Material, Mesh, OrthographicCamera, PlaneGeometry, Scene, WebGLRenderTarget } from 'three'

type MaterialWarmupProps = {
    materials: readonly Material[]
    prepareMaterial?: (material: Material) => void
}

export function MaterialWarmup({
    materials,
    prepareMaterial,
}: MaterialWarmupProps) {
    const gl = useThree((state) => state.gl)
    const warmedUpRef = useRef(false)

    useEffect(() => {
        if (warmedUpRef.current || materials.length === 0) {
            return
        }

        let disposed = false

        const warmup = async () => {
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve())
            })

            if (disposed || warmedUpRef.current) {
                return
            }

            const scene = new Scene()
            const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10)
            camera.position.z = 1
            camera.updateProjectionMatrix()
            camera.updateMatrixWorld(true)

            const geometry = new PlaneGeometry(2, 2)
            const mesh = new Mesh(geometry, materials[0])
            mesh.frustumCulled = false
            scene.add(mesh)
            scene.updateMatrixWorld(true)

            const renderTarget = new WebGLRenderTarget(1, 1)
            const previousRenderTarget = gl.getRenderTarget()
            const renderer = gl as typeof gl & {
                compileAsync?: (scene: Scene, camera: OrthographicCamera) => Promise<void>
            }

            try {
                for (const material of materials) {
                    if (disposed) {
                        return
                    }

                    prepareMaterial?.(material)
                    mesh.material = material

                    if (renderer.compileAsync) {
                        await renderer.compileAsync(scene, camera)
                    }

                    gl.setRenderTarget(renderTarget)
                    gl.render(scene, camera)
                }

                warmedUpRef.current = true
            } finally {
                gl.setRenderTarget(previousRenderTarget)
                renderTarget.dispose()
                geometry.dispose()
            }
        }

        void warmup()

        return () => {
            disposed = true
        }
    }, [gl, materials, prepareMaterial])

    return null
}

